/**
 * Client-side fallback when GET .../configuration is unavailable (404 / offline).
 * Still renders sql_fusion_reference overview from existing-approvals + catalog hints.
 */
import { textMatchesTask } from './bpmTaskTenantMatch';

const CANONICAL_GLOBAL_ABSENCE_CODE = '30000003500603';
const FUSION_TASK_CODE_ALIASES = {
  '300000083500603': CANONICAL_GLOBAL_ABSENCE_CODE,
  '300000082927718': CANONICAL_GLOBAL_ABSENCE_CODE,
};

function normalizeTaskCode(task) {
  const raw = task?.code ? String(task.code).trim() : '';
  if (!raw || !/^\d{10,}$/.test(raw)) {
    return /global\s*absence/i.test(task?.name || '') ? CANONICAL_GLOBAL_ABSENCE_CODE : null;
  }
  if (FUSION_TASK_CODE_ALIASES[raw]) return FUSION_TASK_CODE_ALIASES[raw];
  if (/global\s*absence/i.test(task?.name || '') && raw === '300000083500603') {
    return CANONICAL_GLOBAL_ABSENCE_CODE;
  }
  return raw;
}

const ABSENCE_REFERENCE = {
  label: 'Absence',
  transactionHint: 'Global Absence Recording',
  policyTemplate:
    "For absence requests over 3 calendar days, route sequentially to the submitter's line manager, then to the HR Absence approval group.",
  sampleStages: [
    { order: 1, routing: 'sequential', approvers: [{ kind: 'lineManager', ref: 'n/a' }] },
    {
      order: 2,
      routing: 'sequential',
      approvers: [{ kind: 'approvalGroup', ref: 'HR_ABSENCE_APPROVERS' }],
    },
  ],
  note: 'Reference design from product catalog — compare with tenant SQL and Fusion configuration.',
};

function inferCategory(task) {
  if (task?.category) return task.category;
  const n = String(task?.name || '').toLowerCase();
  if (/global\s*absence|absence|leave|pto/.test(n)) return 'absence';
  if (/time\s*card|timecard|time\s*entry|labor/.test(n)) return 'time';
  return null;
}

function describeNoSqlMatch(task, existingApprovals) {
  const src = existingApprovals?.source;
  const absCount = existingApprovals?.absences?.length ?? 0;
  const msg = existingApprovals?.message || '';
  const codeNote =
    task?.code && normalizeTaskCode(task) !== String(task.code).trim()
      ? ` (catalog code ${task.code} normalized to ${normalizeTaskCode(task)} for matching)`
      : '';
  if (src === 'mock' || !existingApprovals) {
    return `Tenant SQL did not match «${task.name}»${codeNote}. BIP SQL runner may be unset or existing-approvals API failed — showing reference design.`;
  }
  if (absCount === 0 && /AI_SQL_RUNNER|not installed|not configured/i.test(msg)) {
    return `No HRC_ARM rows — ${msg || 'configure AI_SQL_RUNNER_REPORT_PATH on the API server.'}${codeNote}`;
  }
  if (absCount === 0) {
    return `HRC_ARM query returned 0 absence rows for this pod — no process matched «${task.name}»${codeNote}. Showing reference design.`;
  }
  return `Tenant SQL did not match «${task.name}» among ${absCount} absence row(s)${codeNote}. Showing reference design and Fusion links.`;
}

function pickSqlRow(task, existingApprovals) {
  const cat = inferCategory(task);
  const pool = [];
  if (cat === 'absence' || !cat) pool.push(...(existingApprovals?.absences || []));
  if (cat === 'time' || !cat) pool.push(...(existingApprovals?.timeLabor || []));
  const code = normalizeTaskCode(task);
  return pool.find(
    (row) =>
      textMatchesTask(row.name, task, { loose: true }) ||
      textMatchesTask(row.taskDefinitionName, task, { loose: true }) ||
      textMatchesTask(row.description, task, { loose: true }) ||
      (code &&
        [row.code, row.taskDefinitionName, row.name]
          .filter(Boolean)
          .some((f) => String(f).includes(code)))
  );
}

function fusionLinksForTask(task, baseUrl) {
  const host = baseUrl ? String(baseUrl).replace(/\/+$/, '') : null;
  const code =
    task?.code && /^\d{10,}$/.test(String(task.code).trim())
      ? String(task.code).trim()
      : /global\s*absence/i.test(task?.name || '')
        ? '30000003500603'
        : null;
  const navigatorPath =
    'Tools → Transaction Console → Global Absence Recording → Manage Task Configuration';
  const links = [];
  if (host) {
    links.push({ id: 'fusion-home', label: 'Open Fusion', url: `${host}/fscmUI/faces/FuseWelcome` });
    links.push({
      id: 'transaction-console',
      label: 'Transaction Console',
      url: `${host}/fscmUI/faces/FuseOverview?fndGlobalItemNodeId=itemNode_workflow_workflow_transaction_console`,
    });
    if (code) {
      links.push({
        id: 'manage-task-config',
        label: 'Manage Task Configuration',
        url: `${host}/fscmUI/faces/deeplink?objType=FND_TASK&action=RUN_TASK&taskId=${encodeURIComponent(code)}`,
        taskId: code,
      });
    }
  }
  return { host, navigatorPath, taskId: code, links, fullHint: host ? `${host} — Navigator → ${navigatorPath}` : navigatorPath };
}

/**
 * @param {object} task
 * @param {object} [taskConfigPayload] — /api/copilot/bpm/task-config
 * @param {object} [existingApprovals] — /api/copilot/bpm/existing-approvals
 * @param {string} [fusionBaseUrl]
 */
export function buildFallbackTaskConfiguration(task, taskConfigPayload, existingApprovals, fusionBaseUrl) {
  const taskForMatch = {
    ...task,
    code: normalizeTaskCode(task) || task?.code,
  };
  const row = pickSqlRow(taskForMatch, existingApprovals);
  const cat = inferCategory(task);
  const fusionLinks = fusionLinksForTask(task, fusionBaseUrl);
  const referenceDesign = cat === 'absence' ? ABSENCE_REFERENCE : null;
  const hasSqlRow = !!row;

  return {
    taskKey: String(task.name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-'),
    displayMode: 'sql_fusion_reference',
    sourceBadge: hasSqlRow ? 'sql' : 'catalog',
    summary: hasSqlRow
      ? `Matched «${row.name}» from tenant SQL (offline configuration API).`
      : describeNoSqlMatch(taskForMatch, existingApprovals),
    tenantSql: row
      ? {
          compositeName: row.name,
          taskDefinitionName: row.taskDefinitionName,
          description: row.description,
          status: row.status,
          definition: row.description,
        }
      : null,
    referenceDesign,
    fusionLinks,
    fusionUrls: fusionLinks,
    tenantSlice: taskConfigPayload?.slice || { hasMatch: hasSqlRow, rules: [], matchTier: hasSqlRow ? 'sql' : 'none' },
    dataSources: { displayMode: 'sql_fusion_reference', endpointProbe: [], sourceBadge: hasSqlRow ? 'sql' : 'catalog' },
    tabs: { configuration: { endpointProbe: [] } },
    task: { ...task, confirmedOnTenant: hasSqlRow },
    _fallback: true,
  };
}
